import {
  closestCenter,
  pointerWithin,
  rectIntersection,
  type Active,
  type Announcements,
  type CollisionDetection,
  type KeyboardCoordinateGetter,
  type Over,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { COLUMNS, columnTitle, type BoardCardData, type ColumnKey } from './boardModel';

/**
 * dnd-kit wiring for the board. Two kinds of drop target: the columns (`col-<key>`) and, inside
 * each, the sortable cards (`card-<id>`). Collision is two-step: first the column (the pointer's
 * column for mouse/touch, the column under the card's centre for keyboard), then the card closest
 * to the dragged card's centre among THAT column's cards; an empty or collapsed column is the
 * target itself. The keyboard moves a card one COLUMN per ←/→ press and one place per ↑/↓ press.
 * Columns are not always one row: on a medium-width page the "No workflow label" lane spans the
 * top and the workflow columns sit below it, so ←/→ walk the columns in board order (not by x)
 * and both axes count when finding the column under the card.
 */

type Box = { left: number; top: number; width: number; height: number };
const centreX = (r: { left: number; width: number }) => r.left + r.width / 2;
const centreY = (r: { top: number; height: number }) => r.top + r.height / 2;
const within = (r: Box, x: number, y: number) =>
  x >= r.left && x <= r.left + r.width && y >= r.top && y <= r.top + r.height;
const ORDER = COLUMNS.map((c) => c.key);
/** Extra hit area (px) around the collapsed "No workflow label" strip; it looks the same. */
export const STRIP_SLOP = 12;
type Container = { id: UniqueIdentifier; data: { current?: unknown } };
/** The column a drop target belongs to (a column's own key, or the column a card sits in). */
export const columnOf = (c: { data: { current?: unknown } } | null | undefined): ColumnKey | undefined =>
  (c?.data.current as { column?: ColumnKey } | undefined)?.column;
const isColumn = (c: Container) => String(c.id).startsWith('col-');
const orderOf = (c: Container) => ORDER.indexOf(columnOf(c) ?? 'none');
/** The task id of a card drop target (`card-12` → 12), null for a column. */
export const cardIdOf = (id: UniqueIdentifier | null | undefined): number | null => {
  const m = /^card-(\d+)$/.exec(String(id ?? ''));
  return m ? Number(m[1]) : null;
};

/** ←/→ jump to the previous/next column; ↑/↓ move one place within the column the card is over. */
let lastJump: { active: UniqueIdentifier; fromCx: number; col: UniqueIdentifier } | null = null;
/** Call on every drag start, so a cancelled drag's last jump is not reused. */
export const resetKeyboardJump = () => {
  lastJump = null;
};

export const columnKeyboardCoordinates: KeyboardCoordinateGetter = (event, { active, currentCoordinates, context }) => {
  const key = event.code || event.key;
  const dir = key === 'ArrowRight' ? 1 : key === 'ArrowLeft' ? -1 : 0;
  if (key === 'ArrowUp' || key === 'ArrowDown') {
    event.preventDefault();
    // The stock sortable getter, limited to the cards of the column the card is over now (it
    // would otherwise also see the columns and the cards of the columns beside it).
    const column = columnOf(context.over) ?? columnOf(context.active);
    const enabled = context.droppableContainers.getEnabled().filter((c) => !isColumn(c) && columnOf(c) === column);
    const scoped = {
      getEnabled: () => enabled,
      get: (id: UniqueIdentifier) => context.droppableContainers.get(id),
    } as unknown as typeof context.droppableContainers;
    return (
      sortableKeyboardCoordinates(event, { active, currentCoordinates, context: { ...context, droppableContainers: scoped } }) ??
      currentCoordinates
    );
  }
  if (!dir) return undefined;
  event.preventDefault();
  // Droppable rects are measured only once a drag is under way (MeasuringStrategy.WhileDragging),
  // so an arrow pressed right after Space can arrive before that first measure: then the getter
  // saw no columns, returned the same spot, and the card stayed "over TODO". Measure the DOM
  // directly for anything dnd-kit has not measured yet.
  const live = (n: HTMLElement | null | undefined): Box | null => {
    const b = n?.getBoundingClientRect();
    return b && (b.width || b.height) ? { left: b.left, top: b.top, width: b.width, height: b.height } : null;
  };
  const card: Box | null = context.collisionRect ?? live(context.activeNode);
  if (!card) return currentCoordinates;
  const cols = context.droppableContainers
    .getEnabled()
    .filter(isColumn)
    .map((c) => ({ c, r: context.droppableRects.get(c.id) ?? live(c.node.current) }))
    .filter((x): x is { c: (typeof x)['c']; r: NonNullable<(typeof x)['r']> } => !!x.r)
    .sort((a, b) => orderOf(a.c) - orderOf(b.c));
  if (!cols.length) return currentCoordinates;
  const cx = centreX(card);
  const cy = centreY(card);
  // Two quick presses can arrive before the card's rect is re-measured: then the rect still
  // shows the old spot, and the column we sent it to last time is the real starting point.
  let i =
    lastJump && lastJump.active === active && Math.abs(lastJump.fromCx - cx) < 1
      ? cols.findIndex((x) => x.c.id === lastJump?.col)
      : -1;
  if (i < 0) i = cols.findIndex((x) => within(x.r, cx, cy));
  if (i < 0) i = cols.findIndex((x) => cx >= x.r.left && cx <= x.r.left + x.r.width);
  if (i < 0) i = cols.reduce((best, x, j) => (Math.abs(centreX(x.r) - cx) < Math.abs(centreX(cols[best].r) - cx) ? j : best), 0);
  const next = cols[Math.min(cols.length - 1, Math.max(0, i + dir))];
  lastJump = { active, fromCx: cx, col: next.c.id };
  // Keyboard collision picks the column under the card's centre. In a one-row layout the card
  // only moves sideways; it moves vertically only when the next column is in another row.
  const dy = cy >= next.r.top && cy <= next.r.top + next.r.height ? 0 : centreY(next.r) - cy;
  return { x: currentCoordinates.x + (centreX(next.r) - cx), y: currentCoordinates.y + dy };
};

/** The column hit: mouse/touch = the column under the pointer; keyboard = the column under the card's centre. */
const columnHit: CollisionDetection = (args) => {
  const cols = args.droppableContainers.filter(isColumn);
  const scoped = { ...args, droppableContainers: cols };
  if (args.pointerCoordinates) {
    const hit = pointerWithin(scoped);
    if (hit.length) return hit;
    // The collapsed strip is only 44px wide: it also takes a pointer in the gap around it.
    const { x, y } = args.pointerCoordinates;
    for (const c of cols) {
      const r = args.droppableRects.get(c.id);
      if (!r || !(c.data.current as { collapsed?: boolean } | undefined)?.collapsed) continue;
      const s = STRIP_SLOP;
      if (within({ left: r.left - s, top: r.top - s, width: r.width + 2 * s, height: r.height + 2 * s }, x, y)) {
        return [{ id: c.id, data: { droppableContainer: c, value: 0 } }];
      }
    }
    return rectIntersection(scoped);
  }
  const cx = centreX(args.collisionRect);
  const cy = centreY(args.collisionRect);
  const hit = (test: (r: Box) => boolean) => {
    for (const c of cols) {
      const r = args.droppableRects.get(c.id);
      if (r && test(r)) return [{ id: c.id, data: { droppableContainer: c, value: 0 } }];
    }
    return null;
  };
  return hit((r) => within(r, cx, cy)) ?? hit((r) => cx >= r.left && cx <= r.left + r.width) ?? rectIntersection(scoped);
};

/** First the column, then the closest of its cards (an empty or collapsed column is the target itself). */
export const boardCollision: CollisionDetection = (args) => {
  const [first] = columnHit(args);
  if (!first) return [];
  const column = columnOf(args.droppableContainers.find((c) => c.id === first.id));
  const cards = args.droppableContainers.filter((c) => !isColumn(c) && columnOf(c) === column);
  if (!cards.length) return [first];
  const near = closestCenter({ ...args, droppableContainers: cards });
  return near.length ? near : [first];
};

/** Is the dragged card's centre below the centre of the card it is over (drop after it)? */
export function isBelow(active: Pick<Active, 'rect'>, over: Pick<Over, 'rect'> | null): boolean {
  const r = active.rect.current.translated;
  if (!r || !over) return false;
  return centreY(r) > centreY(over.rect);
}

/** Where a drop lands: the column, the final index, and the visible neighbours sent to the api. */
export interface DropPlan {
  to: ColumnKey;
  /** Index of the card in the column's final list. */
  index: number;
  /** Cards in the column after the drop. */
  n: number;
  afterId: number | null;
  beforeId: number | null;
  same: boolean;
  /** Same column, same place: nothing to send. */
  unchanged: boolean;
}

/**
 * Plan a drop from what the viewer sees. Same column: the sortable rule (the card takes the
 * place of the card it is over). Another column: before the card it is over, or after it when
 * the dragged card's centre is below that card's centre. Over a column with no visible card
 * (empty, or the collapsed "No workflow label" strip): the bottom, and no neighbours are sent.
 */
export function planDrop(
  lists: Record<ColumnKey, readonly { id: number }[]>,
  cardId: number,
  from: ColumnKey,
  to: ColumnKey,
  overCardId: number | null,
  below: boolean,
): DropPlan {
  const ids = lists[to].map((c) => c.id);
  const own = ids.indexOf(cardId);
  if (from === to && own >= 0) {
    const at = overCardId == null ? -1 : ids.indexOf(overCardId);
    const index = at < 0 ? own : at;
    const rest = ids.filter((id) => id !== cardId);
    rest.splice(index, 0, cardId);
    return {
      to,
      index,
      n: rest.length,
      afterId: rest[index - 1] ?? null,
      beforeId: rest[index + 1] ?? null,
      same: true,
      unchanged: index === own,
    };
  }
  const rest = ids.filter((id) => id !== cardId);
  const at = overCardId == null ? -1 : rest.indexOf(overCardId);
  if (at < 0) return { to, index: rest.length, n: rest.length + 1, afterId: null, beforeId: null, same: false, unchanged: false };
  const index = at + (below ? 1 : 0);
  rest.splice(index, 0, cardId);
  return {
    to,
    index,
    n: rest.length,
    afterId: rest[index - 1] ?? null,
    beforeId: rest[index + 1] ?? null,
    same: false,
    unchanged: false,
  };
}

type DragData = { card?: BoardCardData; from?: ColumnKey } | undefined;
const who = (d: DragData) => (d?.card ? `task #${d.card.id}, ${d.card.title}` : 'the card');
const spot = (p: DropPlan) => `position ${p.index + 1} of ${p.n} in ${columnTitle(p.to)}`;

export const SR_INSTRUCTIONS = {
  draggable:
    'To move a task, press Space or Enter to pick it up. Use the up and down arrow keys to change its place in the column and the left and right arrow keys to move it to another column, then press Space or Enter to drop it, or Escape to cancel.',
};

/** Screen-reader announcements; `locate` plans the drop for the card and target being announced. */
export function makeAnnouncements(locate: (active: Active, over: Over | null) => DropPlan | null): Announcements {
  return {
    onDragStart: ({ active }) => {
      const d = active.data.current as DragData;
      return `Picked up ${who(d)} from ${d?.from ? columnTitle(d.from) : 'its column'}.`;
    },
    onDragOver: ({ active, over }) => {
      const p = over ? locate(active, over) : null;
      return p ? `${who(active.data.current as DragData)} is over ${spot(p)}.` : `${who(active.data.current as DragData)} is not over a column.`;
    },
    onDragEnd: ({ active, over }) => {
      const p = over ? locate(active, over) : null;
      if (!p) return `${who(active.data.current as DragData)} was dropped outside the columns and stays where it was.`;
      return p.unchanged
        ? `${who(active.data.current as DragData)} stays at ${spot(p)}.`
        : `Dropped ${who(active.data.current as DragData)} at ${spot(p)}.`;
    },
    onDragCancel: ({ active }) => `Cancelled. ${who(active.data.current as DragData)} stays where it was.`,
  };
}
